import { ReactNode } from 'react';
import { calculateDistance } from 'common/units';
import { FormattedMessage, useIntl } from 'react-intl';
import { CallbackTimeslotDto, ReferralDto } from 'common/api';
import {
  displayCallbackTime,
  displayContentValueFormatted,
} from 'utils/format';
import { captureEventV2 } from 'utils/events/captureEvent';
import { Events } from '@clariness/tracking-event-runtime';
import { Store } from 'store';
import {
  Box,
  Button,
  IconButton,
  Tag,
  Text,
  Tooltip,
} from '@clariness/clarikit-react';
import { CopyIcon, MailIcon } from '@clariness/clarikit-icons';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { usePatientDetailsContext } from '../../../../ReferralDetails';

type PatientDetailsContactProps = {
  referral?: ReferralDto;
};

export const PatientDetailsContact = ({
  referral,
}: PatientDetailsContactProps) => {
  const { isExternalReferral } = usePatientDetailsContext();
  const callbackTimeslotSiteSelectedEnabled = useFeatureFlagEnabled(
    'CALLBACK_TIMESLOT_SITE_SELECTED_ENABLED'
  );
  const intl = useIntl();
  const { user } = Store.useUser();
  const address =
    referral?.postalCode &&
    referral.locationBasedOnPostalCode?.includes(referral.postalCode)
      ? referral.locationBasedOnPostalCode
      : `${referral?.postalCode || ''} ${referral?.locationBasedOnPostalCode || ''}`;

  const copyTelephoneLabel = intl.formatMessage({
    id: 'is.patient_details_modal.contact_details.telephone_copy_to_clipboard',
    defaultMessage: 'Copy telephone',
  });
  const copyEmailLabel = intl.formatMessage({
    id: 'is.patient_details_modal.contact_details.email_address_copy_to_clipboard',
    defaultMessage: 'Copy email',
  });

  const rows: { id: string; label: string; value: ReactNode }[] = [
    {
      id: 'appointment',
      label: intl.formatMessage({
        id: 'is.appointment.title',
        defaultMessage: 'Scheduled Appointment Time',
      }),
      value: callbackTimeslotSiteSelectedEnabled ? (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap="16">
          {referral?.siteCallbackTime?.systemSelected ? (
            <>
              <FormattedMessage
                id="is.patient_details_modal.contact_details.availability.anytime"
                defaultMessage="anytime"
              />
              <Tag
                hideCloseButton
                colorVariant="green"
                label={intl.formatMessage({
                  id: 'is.patient_details_modal.system_selected_time',
                  defaultMessage: 'SYSTEM SELECTED',
                })}
              />
            </>
          ) : (
            referral?.siteCallbackTime &&
            referral?.siteCallbackTime?.status !==
              CallbackTimeslotDto.status.PENDING &&
            displayCallbackTime(referral?.siteCallbackTime, intl)
          )}
        </Box>
      ) : (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap="16">
          {displayContentValueFormatted(referral?.appointmentDateTime, intl)}
        </Box>
      ),
    },
    {
      id: 'availability',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.contact_details.patient_availability',
        defaultMessage: 'Availability',
      }),
      value: (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap="16">
          {referral?.availability}
        </Box>
      ),
    },
    {
      id: 'telephone',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.contact_details.telephone',
        defaultMessage: 'Telephone',
      }),
      value: referral?.phoneNumber && (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap="12">
          <span>{referral.phoneNumber}</span>
          <Tooltip content={copyTelephoneLabel} position="north-west">
            <span className="copyIcon16">
              <IconButton
                icon={CopyIcon}
                accessibilityLabel={copyTelephoneLabel}
                onClick={() =>
                  navigator.clipboard.writeText(referral.phoneNumber || '')
                }
              />
            </span>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: 'email',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.contact_details.email',
        defaultMessage: 'Email',
      }),
      value: referral?.patient.email && (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap="12">
          <span>{referral.patient.email}</span>
          <Tooltip content={copyEmailLabel} position="north-west">
            <span className="copyIcon16">
              <IconButton
                icon={CopyIcon}
                accessibilityLabel={copyEmailLabel}
                onClick={() =>
                  navigator.clipboard.writeText(referral.patient.email || '')
                }
              />
            </span>
          </Tooltip>
          <Button
            variant="secondary"
            size="s"
            iconLeft={MailIcon}
            onClick={() => {
              captureEventV2(
                Events.InvestigatorService.InvestigatorEmailPatient({
                  InvestigatorID: user?.id ?? '',
                  PatientID: referral?.id ?? '',
                })
              );
              window.location.href = `mailto:${referral?.patient.email}`;
            }}
          >
            {intl.formatMessage({
              id: 'is.patient_details_modal.contact_details.email_address_button',
              defaultMessage: 'Email',
            })}
          </Button>
        </Box>
      ),
    },
    {
      id: 'address',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.contact_details.address',
        defaultMessage: 'Address',
      }),
      value: address,
    },
    {
      id: 'distance',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.contact_details.distance',
        defaultMessage: 'Distance',
      }),
      value: isExternalReferral ? (
        <FormattedMessage
          id="is.patient_details_modal.contact_details.distance_not_applicable"
          defaultMessage="Not applicable"
        />
      ) : (
        calculateDistance(
          referral?.distanceToPatientInKM,
          referral?.language.code
        )
      ),
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap="8" width="100%">
      <style>
        {`
          .copyIcon16 {
            display: inline-flex;
          }
          .copyIcon16 .Clarikit-Icon {
            width: 16px;
            height: 16px;
          }
        `}
      </style>
      <Text as="h6" variant="headingXs">
        {intl.formatMessage({
          id: 'is.patient_details_modal.contact_details_title',
          defaultMessage: 'Contact Details',
        })}
      </Text>
      <Box width="100%" height="1px" backgroundColor="color-gray-variant-4" />
      <Box as="dl" display="flex" flexDirection="column" gap="16" margin="0">
        {rows.map((row) => (
          <Box
            key={row.id}
            display="flex"
            flexWrap="wrap"
            gap="40"
            width="100%"
          >
            <Box as="dt" style={{ flex: '0 0 150px' }}>
              <Text as="span" variant="bodyMd" fontWeight="bold">
                {row.label}
              </Text>
            </Box>
            <Box as="dd" margin="0" minWidth="0" style={{ flex: '1 1 12rem' }}>
              <Text as="span" variant="bodyMd">
                {row.value}
              </Text>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
